import { z } from 'zod';
import { BLAME_LAYERS, CARE_POLICIES, CARE_TYPES, CATEGORIES, LAUNDRY_GROUPS, LAYERS, SLOTS, SLOT_MODES, TRIP_STATUSES, WEATHER_SOURCES, } from './enums.js';
export const isoInstant = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/, 'ISO-8601 UTC mit Millisekunden erwartet');
export const localDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD erwartet');
export const hexColor = z.string().regex(/^#[0-9a-fA-F]{6}$/, '#RRGGBB erwartet');
export const materialSchema = z.object({
    material: z.string().min(1),
    percent: z.number().min(0).max(100),
});
export const materialsSchema = z.array(materialSchema);
// ---------------------------------------------------------------- items
export const itemInputSchema = z.object({
    name: z.string().min(1).max(200),
    category: z.enum(CATEGORIES),
    subcategory: z.string().max(100).nullish(),
    layer: z.enum(LAYERS),
    brand: z.string().max(100).nullish(),
    size: z.string().max(50).nullish(),
    colorPrimaryHex: hexColor.nullish(),
    colorPrimaryName: z.string().max(50).nullish(),
    colorSecondaryHex: hexColor.nullish(),
    pattern: z.string().max(50).nullish(),
    materials: materialsSchema.nullish(),
    photoId: z.string().uuid().nullish(),
    notes: z.string().max(2000).nullish(),
    warmthPoints: z.number().int().min(0).max(10),
    tempToleranceC: z.number().min(0).max(20).nullish(),
    rainResistance: z.number().int().min(0).max(3).default(0),
    windResistance: z.number().int().min(0).max(3).default(0),
    formality: z.number().int().min(1).max(5),
    carePolicy: z.enum(CARE_POLICIES),
    laundryGroup: z.enum(LAUNDRY_GROUPS),
    washTempC: z.number().int().min(0).max(95).nullish(),
    washCycle: z.string().max(50).nullish(),
    wearsPerWash: z.number().int().min(1).max(100),
    wearsPerClean: z.number().int().min(1).max(200).nullish(),
    needsIroning: z.boolean().default(false),
    dryingHours: z.number().min(0).max(200).default(0),
    restHours: z.number().min(0).max(500).default(0),
    purchaseDate: localDate.nullish(),
    purchasePriceCents: z.number().int().min(0).nullish(),
    currency: z.string().length(3).default('EUR'),
    condition: z.number().int().min(1).max(5).nullish(),
    /** D-35: Steht das Teil zur Auswahl? Socken, Unterwäsche und Sportsachen nicht. */
    suggestable: z.boolean().default(true),
});
export const itemPatchSchema = itemInputSchema.partial();
/**
 * D-41: Kategorie-Startwerte als Daten statt als Quelltext.
 *
 * Gespeichert wird nur die **Abweichung** vom Katalog in `seed-defaults.ts` — dieselbe
 * Bauart wie die Einstellungen (D-21): was im Code steht, ist die Antwort auf „was gilt,
 * solange nichts gesetzt wurde", und Zurücksetzen heißt, die Zeile zu löschen. Eine
 * vollständige Kopie aller 21 Kategorien in der Datenbank wäre eine zweite Wahrheit, die
 * bei jeder Änderung am Katalog auseinanderliefe.
 */
export const categoryDefaultsPatchSchema = z
    .object({
    layer: z.enum(LAYERS),
    warmthPoints: z.number().int().min(0).max(10),
    formality: z.number().int().min(1).max(5),
    carePolicy: z.enum(CARE_POLICIES),
    laundryGroup: z.enum(LAUNDRY_GROUPS),
    wearsPerWash: z.number().int().min(1).max(100),
    wearsPerClean: z.number().int().min(1).max(200).nullable(),
    needsIroning: z.boolean(),
    dryingHours: z.number().min(0).max(200),
    restHours: z.number().min(0).max(500),
    rainResistance: z.number().int().min(0).max(3),
    windResistance: z.number().int().min(0).max(3),
    suggestable: z.boolean(),
})
    .partial();
// ---------------------------------------------------------------- weather
export const exposureWindowSchema = z.object({
    label: z.string(),
    from: z.string().regex(/^\d{2}:\d{2}$/),
    to: z.string().regex(/^\d{2}:\d{2}$/),
    weight: z.number().min(0).max(1),
});
export const exposureWindowResultSchema = exposureWindowSchema.extend({
    apparentC: z.number(),
    temperatureC: z.number(),
    precipProb: z.number().min(0).max(1),
    precipitationMm: z.number().min(0),
    windKmh: z.number().min(0),
});
export const weatherSnapshotSchema = z.object({
    lat: z.number().min(-90).max(90),
    lon: z.number().min(-180).max(180),
    effectiveApparentC: z.number(),
    minApparentC: z.number(),
    maxApparentC: z.number(),
    rainRisk: z.number().min(0).max(1),
    windMaxKmh: z.number().min(0),
    indoorTempC: z.number(),
    indoorWeight: z.number().min(0).max(1),
    windows: z.array(exposureWindowResultSchema),
    fetchedAt: isoInstant,
    provider: z.literal('open-meteo'),
    endpoint: z.enum(['forecast', 'historical-forecast']),
});
// ---------------------------------------------------------------- outfits
export const slotRuleSchema = z.object({
    categories: z.array(z.enum(CATEGORIES)).nonempty().optional(),
    colorFamilies: z.array(z.string()).nonempty().optional(),
    formalityMin: z.number().int().min(1).max(5).optional(),
    formalityMax: z.number().int().min(1).max(5).optional(),
    tags: z.array(z.string()).nonempty().optional(),
});
export const outfitSlotInputSchema = z
    .object({
    slot: z.enum(SLOTS),
    position: z.number().int().min(0).default(0),
    mode: z.enum(SLOT_MODES),
    itemId: z.string().uuid().nullish(),
    rule: slotRuleSchema.nullish(),
    optional: z.boolean().default(false),
    includeBelowTempC: z.number().nullish(),
    includeAboveTempC: z.number().nullish(),
})
    .refine((s) => (s.mode === 'fixed' ? !!s.itemId && !s.rule : !!s.rule && !s.itemId), { message: 'mode=fixed verlangt itemId, mode=rule verlangt rule — nie beides' });
export const outfitInputSchema = z.object({
    name: z.string().min(1).max(200),
    notes: z.string().max(2000).nullish(),
    formalityTarget: z.number().int().min(1).max(5).nullish(),
    slots: z.array(outfitSlotInputSchema).min(1),
});
// ---------------------------------------------------------------- wear & feedback
export const wearLogInputSchema = z.object({
    outfitId: z.string().uuid().nullish(),
    // D-34: Anlässe sind bearbeitbar, also keine Aufzählung mehr. Ob es den Anlass gibt,
    // prüft der Server gegen die Tabelle — ein Zod-Enum wäre dort eine zweite Wahrheit.
    occasion: z.string().min(1).max(60),
    outdoorMinutesPlanned: z.number().int().min(0).max(1440).nullish(),
    items: z
        .array(z.object({ itemId: z.string().uuid(), slot: z.enum(SLOTS) }))
        .min(1),
    /** Nur für Nachträge; Default ist der aktuelle lokale Tag (04:00-Cutoff). */
    localDate: localDate.optional(),
});
export const feedbackInputSchema = z.object({
    thermalVote: z.number().int().min(-3).max(3),
    comfort: z.number().int().min(1).max(5).nullish(),
    lookConfidence: z.number().int().min(1).max(5).nullish(),
    occasionFit: z.number().int().min(1).max(5).nullish(),
    blameLayer: z.enum(BLAME_LAYERS).nullish(),
    note: z.string().max(2000).nullish(),
});
// ---------------------------------------------------------------- wash & care
export const washRunInputSchema = z.object({
    laundryGroup: z.enum(LAUNDRY_GROUPS),
    tempC: z.number().int().min(0).max(95).nullish(),
    program: z.string().max(50).nullish(),
    note: z.string().max(500).nullish(),
    itemIds: z.array(z.string().uuid()).min(1),
});
export const careEventInputSchema = z.object({
    itemIds: z.array(z.string().uuid()).min(1),
    type: z.enum(CARE_TYPES),
    vendor: z.string().max(200).nullish(),
    expectedBackAt: localDate.nullish(),
    costCents: z.number().int().min(0).nullish(),
    note: z.string().max(500).nullish(),
});
// ---------------------------------------------------------------- trips
/** Genau das, was `PushSubscription.toJSON()` im Browser liefert. */
export const pushSubscriptionSchema = z.object({
    endpoint: z.string().url(),
    expirationTime: z.number().nullish(),
    keys: z.object({
        p256dh: z.string().min(1),
        auth: z.string().min(1),
    }),
});
export const tripInputSchema = z
    .object({
    name: z.string().min(1).max(200),
    startDate: localDate,
    endDate: localDate,
    destLat: z.number().min(-90).max(90).nullish(),
    destLon: z.number().min(-180).max(180).nullish(),
    laundryAvailable: z.boolean().default(false),
    status: z.enum(TRIP_STATUSES).default('planned'),
})
    .refine((t) => t.endDate >= t.startDate, { message: 'endDate darf nicht vor startDate liegen' });
// ---------------------------------------------------------------- recommendation
export const scoreTermSchema = z.object({
    key: z.string(),
    value: z.number(),
    weight: z.number(),
    contribution: z.number(),
    /** Klartext, fällt beim Scoring an — nicht nachträglich erzeugt (§6 Schritt 5). */
    explanation: z.string(),
});
export const suggestionItemSchema = z.object({
    itemId: z.string().uuid(),
    name: z.string(),
    slot: z.enum(SLOTS),
    layer: z.enum(LAYERS),
});
export const suggestionSchema = z.object({
    outfitId: z.string().uuid().nullable(),
    outfitName: z.string().nullable(),
    generated: z.boolean(),
    items: z.array(suggestionItemSchema),
    score: z.number(),
    outfitWarmth: z.number(),
    comfortCenterC: z.number(),
    formality: z.number(),
    terms: z.array(scoreTermSchema),
    reasoning: z.string(),
    warnings: z.array(z.string()),
});
export const recommendQuerySchema = z.object({
    occasion: z.string().min(1).max(60).default('office'),
    limit: z.coerce.number().int().min(1).max(10).default(3),
    date: localDate.optional(),
});
// ---------------------------------------------------------------- misc
export const weatherSourceSchema = z.enum(WEATHER_SOURCES);
export const uuidParam = z.object({ id: z.string().uuid() });
export const loginSchema = z.object({ pin: z.string().min(4).max(128) });
//# sourceMappingURL=schemas.js.map